import { useState, useEffect, useRef } from "react";

const gold = "#f5a623", blue = "#3b82f6", blue2 = "#60a5fa";
const green = "#22c55e", red = "#ef4444", purple = "#a78bfa";
const navy = "#0b0f1a", navy2 = "#111827", navy3 = "#1a2236", navy4 = "#212d45";
const white = "#ffffff", textCol = "#cbd5e1", muted = "#64748b";
const border = "rgba(255,255,255,0.07)";

function useReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}
function Reveal({ children, delay = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "none" : "translateY(22px)", transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s` }}>
      {children}
    </div>
  );
}

const ss = {
  section: { marginBottom: 72 },
  eyebrow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  tag: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: blue },
  line: { flex: 1, height: 1, background: border },
  badge: { fontFamily: "monospace", fontSize: "0.68rem", color: muted },
  h2: { fontFamily: "Georgia,serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", fontWeight: 900, color: white, lineHeight: 1.25, marginBottom: 20 },
  prose: { fontSize: "0.97rem", color: textCol, lineHeight: 1.85, marginBottom: 28 },
  hr: { border: "none", borderTop: `1px solid ${border}`, margin: "48px 0" },
};

function Callout({ icon, children, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "20px 24px", margin: "28px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function Pill({ text, color = gold }) {
  return <span style={{ display: "inline-block", fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, color, background: `${color}18`, border: `1px solid ${color}33`, marginRight: 4, marginBottom: 4 }}>{text}</span>;
}

// ── Product Overview Cards ──
function ProductOverview() {
  const [active, setActive] = useState(null);
  const products = [
    {
      icon: "🏦", name: "PPNs", full: "Principal-Protected Notes", color: blue2,
      issuer: "Chartered Banks", regulated: "Banking regulators", cdic: false,
      tagline: "Principal guaranteed. Returns linked to an underlying asset.",
      risk: "Credit risk, liquidity risk, implicit cost drag",
      audience: "Risk-averse investors wanting market upside with no downside to principal",
    },
    {
      icon: "⚡", name: "Hedge Funds", full: "Hedge Funds", color: gold,
      issuer: "Fund Managers", regulated: "Lightly regulated", cdic: false,
      tagline: "Absolute returns via leverage, shorts, and derivatives.",
      risk: "High risk, illiquid, complex strategies, incentive fee conflicts",
      audience: "Accredited investors ($1M+ assets or $200K+ income) or min. $150K investment",
    },
    {
      icon: "🔒", name: "Closed-End", full: "Closed-End Funds", color: purple,
      issuer: "Fund Companies", regulated: "Securities regulators", cdic: false,
      tagline: "Fixed shares. Trades on exchange at supply/demand price.",
      risk: "Discount to NAV is common; market risk; liquidity varies",
      audience: "Exchange investors comfortable with price ≠ NAV",
    },
    {
      icon: "📦", name: "ETFs", full: "Exchange-Traded Funds", color: green,
      issuer: "Fund Providers", regulated: "Securities regulators", cdic: false,
      tagline: "Baskets of securities. Trade like stocks all day long.",
      risk: "Tracking error; market risk; no principal protection",
      audience: "Cost-conscious investors wanting index exposure with intraday liquidity",
    },
    {
      icon: "🛡️", name: "Seg Funds", full: "Segregated Funds", color: "#4ade80",
      issuer: "Life Insurance Companies", regulated: "Provincial insurance regulators (e.g., FSRA)", cdic: false,
      tagline: "Insurance contract + investment. Guarantees + creditor protection.",
      risk: "Higher MER than mutual funds; 10-year lock-in for guarantees",
      audience: "Investors needing estate planning, creditor protection, or a maturity guarantee",
    },
  ];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10, marginBottom: 14 }}>
        {products.map((p, i) => (
          <div key={i} onClick={() => setActive(active === i ? null : i)}
            style={{ background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? p.color + "55" : border}`, borderRadius: 8, padding: "18px 12px", cursor: "pointer", transition: "all 0.2s", textAlign: "center", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: p.color }} />
            <div style={{ fontSize: "1.6rem", marginBottom: 8 }}>{p.icon}</div>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, color: white }}>{p.name}</div>
            <div style={{ fontSize: "0.65rem", color: p.color, marginTop: 3 }}>Click to explore</div>
          </div>
        ))}
      </div>
      {active !== null && (
        <div style={{ background: navy4, border: `1px solid ${products[active].color}44`, borderRadius: 8, padding: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <span style={{ fontSize: "1.5rem" }}>{products[active].icon}</span>
            <div>
              <div style={{ fontFamily: "Georgia,serif", fontSize: "1.1rem", color: white }}>{products[active].full}</div>
              <div style={{ fontSize: "0.78rem", color: products[active].color, fontStyle: "italic" }}>{products[active].tagline}</div>
            </div>
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {[
              ["Issuer", products[active].issuer, blue2],
              ["Regulated By", products[active].regulated, gold],
              ["Key Risk", products[active].risk, red],
              ["Best For", products[active].audience, green],
            ].map(([lbl, val, col]) => (
              <div key={lbl} style={{ background: navy3, borderRadius: 6, padding: "10px 14px", display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: col, width: 100, flexShrink: 0, paddingTop: 2 }}>{lbl}</div>
                <div style={{ fontSize: "0.84rem", color: textCol, flex: 1, lineHeight: 1.6 }}>{val}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── S1: PPNs ──
function PPNDetail() {
  const [costType, setCostType] = useState("explicit");
  const costs = {
    explicit: {
      label: "Explicit Costs", color: red,
      items: [
        { name: "Commission", desc: "Paid to the distributor at time of sale." },
        { name: "Management Fee", desc: "Ongoing fee for managing the linked investment strategy." },
        { name: "Early Redemption Fee", desc: "Penalty for selling before maturity — may result in receiving less than principal." },
        { name: "Structuring Costs", desc: "Costs to design and build the derivative structure that links to the underlying asset." },
      ],
    },
    implicit: {
      label: "Implicit Costs (Hidden)", color: gold,
      items: [
        { name: "Performance Averaging", desc: "Returns may be based on the average monthly value during the term — not the final value. In a rising market, this drags returns significantly below the actual ending price." },
        { name: "Participation Rate", desc: "The PPN may only credit a % of the underlying asset's return (e.g., 60%). If the index rises 20%, you might receive only 12%." },
        { name: "Price Return Only", desc: "PPNs typically link to the price appreciation of the underlying asset — dividends are excluded. The 'Total Return' (with dividends) would be higher." },
      ],
    },
  };
  const cfg = costs[costType];

  return (
    <div style={{ margin: "28px 0" }}>
      {/* Roles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10, marginBottom: 20 }}>
        {[
          { icon: "🏦", role: "Guarantor", desc: "The chartered bank issuing the PPN guarantees return of principal at maturity.", color: blue2 },
          { icon: "🔧", role: "Manufacturer", desc: "Capital Markets division designs the product — chooses term, underlying asset, participation rate.", color: gold },
          { icon: "🤝", role: "Distributor", desc: "Investment dealers and mutual fund dealers who sell the PPN to end investors.", color: purple },
        ].map((r, i) => (
          <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 16px", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: r.color }} />
            <div style={{ fontSize: "1.3rem", marginBottom: 8 }}>{r.icon}</div>
            <div style={{ fontSize: "0.82rem", fontWeight: 700, color: r.color, marginBottom: 4 }}>{r.role}</div>
            <div style={{ fontSize: "0.8rem", color: muted, lineHeight: 1.65 }}>{r.desc}</div>
          </div>
        ))}
      </div>
      {/* Cost toggle */}
      <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, marginBottom: 16 }}>💸 PPN Cost Breakdown</div>
        <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
          {Object.entries(costs).map(([key, val]) => (
            <button key={key} onClick={() => setCostType(key)} style={{ flex: 1, padding: "10px 14px", background: costType === key ? navy4 : navy3, border: `1px solid ${costType === key ? val.color + "55" : border}`, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700, color: costType === key ? val.color : muted, transition: "all 0.2s" }}>{val.label}</button>
          ))}
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {cfg.items.map((item, i) => (
            <div key={i} style={{ background: navy4, borderLeft: `3px solid ${cfg.color}`, borderRadius: 6, padding: "12px 16px" }}>
              <div style={{ fontSize: "0.82rem", fontWeight: 700, color: white, marginBottom: 4 }}>{item.name}</div>
              <div style={{ fontSize: "0.8rem", color: muted, lineHeight: 1.65 }}>{item.desc}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Risk summary */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10, marginTop: 14 }}>
        {[
          { icon: "⚠️", risk: "Credit Risk", body: "The guarantee is only as strong as the bank behind it. If the issuing bank fails, the guarantee fails.", color: red },
          { icon: "🔒", risk: "Liquidity Risk", body: "Designed to be held to maturity. Selling early may result in getting back less than principal.", color: gold },
          { icon: "🚫", risk: "No CDIC Coverage", body: "PPNs are not deposit instruments — not covered by Canada Deposit Insurance Corporation.", color: purple },
        ].map((r, i) => (
          <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "16px 16px" }}>
            <div style={{ fontSize: "1.1rem", marginBottom: 6 }}>{r.icon}</div>
            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: r.color, marginBottom: 4 }}>{r.risk}</div>
            <div style={{ fontSize: "0.78rem", color: muted, lineHeight: 1.65 }}>{r.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── S2: Hedge Funds ──
function HedgeStrategies() {
  const [active, setActive] = useState(0);
  const strategies = [
    {
      icon: "⚖️", name: "Relative Value", color: green, exposure: "Low",
      exposureScore: 15,
      desc: "Exploits price inefficiencies or mispricings between related securities. The manager simultaneously buys an underpriced security and sells an overpriced one — reducing net market exposure.",
      examples: ["Fixed-income arbitrage", "Convertible arbitrage", "Statistical arbitrage"],
      risk: "Low market exposure — returns depend on the spread closing, not market direction.",
    },
    {
      icon: "🏢", name: "Event-Driven", color: gold, exposure: "Medium",
      exposureScore: 50,
      desc: "Profits from specific corporate events — mergers, acquisitions, spin-offs, bankruptcies, or restructurings. The fund takes positions based on expected event outcomes.",
      examples: ["Merger arbitrage", "Distressed debt investing", "Spin-off investing"],
      risk: "Medium market exposure — outcomes depend on the event occurring as expected.",
    },
    {
      icon: "🧭", name: "Directional", color: red, exposure: "High",
      exposureScore: 85,
      desc: "Bets on the direction of markets, sectors, or individual securities. Uses long (buy) and short (sell) positions to profit from both rising and falling prices.",
      examples: ["Long/Short Equity", "Global Macro", "Managed Futures"],
      risk: "High market exposure — returns are driven by correctly predicting market direction.",
    },
  ];
  const s = strategies[active];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "flex", gap: 2 }}>
        {strategies.map((st, i) => (
          <button key={i} onClick={() => setActive(i)} style={{ flex: 1, padding: "10px 10px", background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? st.color + "44" : border}`, borderBottom: active === i ? `2px solid ${st.color}` : `1px solid ${border}`, borderRadius: "6px 6px 0 0", cursor: "pointer", fontFamily: "inherit", fontSize: "0.76rem", fontWeight: 700, color: active === i ? st.color : muted, transition: "all 0.2s" }}>
            {st.icon} {st.name}
          </button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${s.color}22`, borderRadius: "0 0 8px 8px", padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 4 }}>{s.name} Strategy</div>
            <div style={{ fontSize: "0.82rem", color: muted }}>{s.desc}</div>
          </div>
          <div style={{ textAlign: "center", minWidth: 100 }}>
            <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, marginBottom: 4 }}>Market Exposure</div>
            <div style={{ fontFamily: "monospace", fontSize: "1.4rem", fontWeight: 700, color: s.color }}>{s.exposure}</div>
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, marginBottom: 6 }}>Market Exposure Level</div>
          <div style={{ height: 8, background: navy3, borderRadius: 4, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${s.exposureScore}%`, background: `linear-gradient(90deg,${green},${s.color})`, borderRadius: 4, transition: "width 0.5s ease" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.62rem", color: muted, marginTop: 4 }}>
            <span>Low (Relative Value)</span><span>High (Directional)</span>
          </div>
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ background: navy3, borderRadius: 6, padding: "10px 14px" }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: blue2, marginBottom: 6 }}>Examples</div>
            {s.examples.map((ex, i) => <div key={i} style={{ fontSize: "0.83rem", color: textCol }}>→ {ex}</div>)}
          </div>
          <div style={{ background: navy3, borderLeft: `3px solid ${s.color}`, borderRadius: 6, padding: "10px 14px" }}>
            <div style={{ fontSize: "0.82rem", color: muted }}>{s.risk}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HedgeFeeCalc() {
  const [startNAV, setStartNAV] = useState(10.00);
  const [endNAV, setEndNAV] = useState(12.00);
  const [hurdleRate, setHurdleRate] = useState(5);
  const [incentivePct, setIncentivePct] = useState(20);
  const [result, setResult] = useState(null);

  function calc() {
    const s = parseFloat(startNAV), e = parseFloat(endNAV);
    const hr = parseFloat(hurdleRate) / 100, ip = parseFloat(incentivePct) / 100;
    const totalReturn = ((e - s) / s) * 100;
    const hurdleAmount = s * hr;
    const thresholdNAV = s + hurdleAmount;
    const excessProfit = Math.max(0, e - thresholdNAV);
    const fee = excessProfit * ip;
    const investorKeeps = e - s - fee;
    setResult({ s, e, totalReturn: totalReturn.toFixed(2), hurdleAmount: hurdleAmount.toFixed(2), thresholdNAV: thresholdNAV.toFixed(2), excessProfit: excessProfit.toFixed(2), fee: fee.toFixed(2), investorKeeps: investorKeeps.toFixed(2) });
  }

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🧮 Hedge Fund Incentive Fee Calculator</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "8px 14px", marginBottom: 20, lineHeight: 1.9 }}>
        Hurdle Amount = Start NAV × Hurdle Rate<br />
        Excess Profit = End NAV − (Start NAV + Hurdle Amount)<br />
        Incentive Fee = Excess Profit × Fee %
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10, marginBottom: 16 }}>
        {[["Start NAV ($)", startNAV, setStartNAV, "any"], ["End NAV ($)", endNAV, setEndNAV, "any"], ["Hurdle Rate (%)", hurdleRate, setHurdleRate, "0.1"], ["Incentive Fee (%)", incentivePct, setIncentivePct, "0.1"]].map(([lbl, val, set, step], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" step={step} value={val} onChange={e => set(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "11px 13px", color: white, fontFamily: "monospace", fontSize: "0.88rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate →</button>
      {result && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 10, marginBottom: 14 }}>
            {[
              ["Total Return", `${result.totalReturn}%`, blue2],
              ["Incentive Fee", `$${result.fee}`, red],
              ["Investor Keeps", `$${result.investorKeeps}`, green],
            ].map(([lbl, val, col]) => (
              <div key={lbl} style={{ background: navy4, border: `1px solid ${col}33`, borderRadius: 8, padding: "12px 14px", textAlign: "center" }}>
                <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: col, marginBottom: 4 }}>{lbl}</div>
                <div style={{ fontFamily: "monospace", fontSize: "1.2rem", fontWeight: 700, color: white }}>{val}</div>
              </div>
            ))}
          </div>
          <div style={{ background: navy4, borderRadius: 8, padding: 16 }}>
            <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: gold, marginBottom: 10 }}>Step-by-Step Working</div>
            {[
              `Total return: ($${result.e} − $${result.s}) ÷ $${result.s} × 100 = ${result.totalReturn}%`,
              `Hurdle amount: $${result.s} × ${hurdleRate}% = $${result.hurdleAmount}`,
              `Threshold NAV (must exceed to charge fee): $${result.s} + $${result.hurdleAmount} = $${result.thresholdNAV}`,
              `Excess profit above hurdle: $${result.e} − $${result.thresholdNAV} = $${result.excessProfit}`,
              `Incentive fee: $${result.excessProfit} × ${incentivePct}% = $${result.fee} per unit`,
              `Investor keeps: $${(result.e - result.s).toFixed(2)} gain − $${result.fee} fee = $${result.investorKeeps} net gain`,
            ].map((step, i) => (
              <div key={i} style={{ fontFamily: "monospace", fontSize: "0.79rem", color: i >= 4 ? textCol : muted, padding: "4px 0", display: "flex", gap: 8 }}>
                <span style={{ color: gold, flexShrink: 0 }}>→</span>{step}
              </div>
            ))}
          </div>
          <Callout icon="💧" accent={blue}>
            <strong style={{ color: blue2 }}>High-Water Mark:</strong> If the fund drops below its previous peak, the manager earns zero incentive fee until the NAV surpasses that high-water mark again. This ensures the manager is paid only on <strong>new profits</strong> — not for simply recovering previous losses.
          </Callout>
        </div>
      )}
    </div>
  );
}

function HedgeAccessPanel() {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12, margin: "28px 0" }}>
      {[
        {
          icon: "💎", title: "Accredited Investor", color: gold,
          criteria: ["Financial assets > $1,000,000, OR", "Net income > $200,000/yr (individual), OR", "> $300,000/yr (with spouse)"],
          note: "Exempt from prospectus requirement. Can invest in hedge funds directly.",
        },
        {
          icon: "💵", title: "Minimum Investment", color: blue2,
          criteria: ["Single investment of at least $150,000", "No income or net worth test required", "Based purely on dollar amount committed"],
          note: "Another prospectus exemption route into hedge funds.",
        },
        {
          icon: "🌊", title: "Liquid Alts", color: purple,
          criteria: ["Alternative Mutual Funds — retail accessible", "Use hedge-fund strategies (leverage, shorting)", "Within a regulated mutual fund structure"],
          note: "Allows everyday investors access to hedge fund strategies with daily liquidity and prospectus protection.",
        },
      ].map((card, i) => (
        <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "20px 18px", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: card.color }} />
          <div style={{ fontSize: "1.4rem", marginBottom: 10 }}>{card.icon}</div>
          <div style={{ fontSize: "0.85rem", fontWeight: 700, color: white, marginBottom: 10 }}>{card.title}</div>
          {card.criteria.map((c, j) => (
            <div key={j} style={{ fontSize: "0.78rem", color: textCol, marginBottom: 4, paddingLeft: 10 }}>• {c}</div>
          ))}
          <div style={{ marginTop: 10, padding: "8px 12px", background: navy4, borderRadius: 6, fontSize: "0.75rem", color: muted, lineHeight: 1.6 }}>💡 {card.note}</div>
        </div>
      ))}
    </div>
  );
}

// ── S3: Closed-End Funds ──
function ClosedEndViz() {
  const [navpu, setNavpu] = useState(20.00);
  const [mktPrice, setMktPrice] = useState(17.50);
  const diff = mktPrice - navpu;
  const pct = ((diff / navpu) * 100).toFixed(1);
  const isDiscount = diff < 0;
  const isPremium = diff > 0;
  const status = isPremium ? { label: "Trading at PREMIUM", color: gold, icon: "📈", desc: "Market price exceeds NAV — investors are paying more than the underlying assets are worth. Rare for closed-end funds." }
    : isDiscount ? { label: "Trading at DISCOUNT", color: red, icon: "📉", desc: "Market price is below NAV — investors can buy the underlying assets for less than their actual value. Common for closed-end funds due to limited liquidity and investor sentiment." }
    : { label: "Trading at PAR", color: green, icon: "⚖️", desc: "Market price equals NAV exactly." };

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🔒 Closed-End Fund — Premium / Discount Visualizer</div>
      <p style={{ fontSize: "0.85rem", color: muted, marginBottom: 20, lineHeight: 1.65 }}>
        Closed-end fund shares trade on an exchange based on supply and demand — not NAV. Enter the NAVPU and current market price to see if the fund trades at a discount or premium.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
        {[["NAVPU ($) — Calculated Value", navpu, setNavpu], ["Market Price ($) — Exchange Price", mktPrice, setMktPrice]].map(([lbl, val, set], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" step="0.01" value={val} onChange={e => set(parseFloat(e.target.value) || 0)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "11px 13px", color: white, fontFamily: "monospace", fontSize: "0.9rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "stretch", flexWrap: "wrap" }}>
        {[["NAV Per Unit", `$${navpu.toFixed(2)}`, blue2], ["Market Price", `$${mktPrice.toFixed(2)}`, status.color], ["Difference", `${diff >= 0 ? "+" : ""}$${diff.toFixed(2)} (${pct}%)`, status.color]].map(([lbl, val, col]) => (
          <div key={lbl} style={{ background: navy4, border: `1px solid ${col}33`, borderRadius: 8, padding: "14px 16px", textAlign: "center", flex: 1, minWidth: 100 }}>
            <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: col, marginBottom: 4 }}>{lbl}</div>
            <div style={{ fontFamily: "monospace", fontSize: "1rem", fontWeight: 700, color: white }}>{val}</div>
          </div>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${status.color}44`, borderRadius: 8, padding: "16px 20px" }}>
        <div style={{ fontSize: "0.85rem", fontWeight: 700, color: status.color, marginBottom: 6 }}>{status.icon} {status.label}</div>
        <div style={{ fontSize: "0.84rem", color: textCol, lineHeight: 1.7 }}>{status.desc}</div>
      </div>
    </div>
  );
}

// ── S4: ETFs ──
function ETFTrackingCalc() {
  const [indexRet, setIndexRet] = useState(10.5);
  const [etfRet, setEtfRet] = useState(10.2);
  const [result, setResult] = useState(null);

  function calc() {
    const idx = parseFloat(indexRet), etf = parseFloat(etfRet);
    const te = (idx - etf).toFixed(3);
    const quality = Math.abs(parseFloat(te)) <= 0.1 ? { label: "Excellent Tracking", color: green }
      : Math.abs(parseFloat(te)) <= 0.3 ? { label: "Good Tracking", color: blue2 }
      : Math.abs(parseFloat(te)) <= 0.5 ? { label: "Moderate Tracking Error", color: gold }
      : { label: "High Tracking Error", color: red };
    setResult({ idx, etf, te, quality });
  }

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "28px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🧮 ETF Tracking Error Calculator</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.8rem", color: gold, background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, padding: "6px 14px", marginBottom: 20, display: "inline-block" }}>
        Tracking Error = Index Return − ETF Return
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
        {[["Index Return (%)", indexRet, setIndexRet], ["ETF Return (%)", etfRet, setEtfRet]].map(([lbl, val, set], i) => (
          <div key={i}>
            <label style={{ display: "block", fontSize: "0.65rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{lbl}</label>
            <input type="number" step="0.1" value={val} onChange={e => set(e.target.value)}
              style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "11px 13px", color: white, fontFamily: "monospace", fontSize: "0.9rem", outline: "none" }} />
          </div>
        ))}
      </div>
      <button onClick={calc} style={{ background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>Calculate →</button>
      {result && (
        <div style={{ background: navy4, border: `1px solid ${result.quality.color}33`, borderRadius: 8, padding: 20, marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: gold, marginBottom: 4 }}>Tracking Error</div>
              <div style={{ fontFamily: "monospace", fontSize: "2rem", fontWeight: 900, color: white }}>{result.te}%</div>
            </div>
            <span style={{ fontSize: "0.72rem", fontWeight: 700, padding: "4px 14px", borderRadius: 100, color: result.quality.color, background: `${result.quality.color}18`, border: `1px solid ${result.quality.color}33` }}>{result.quality.label}</span>
          </div>
          <div style={{ fontFamily: "monospace", fontSize: "0.79rem", color: textCol, borderTop: `1px solid ${border}`, paddingTop: 10 }}>
            → {result.idx}% (index) − {result.etf}% (ETF) = {result.te}% tracking error<br />
            → Caused by MER, transaction costs, and cash drag from uninvested dividends
          </div>
        </div>
      )}
    </div>
  );
}

function ETFFeatureGrid() {
  const features = [
    { icon: "💰", title: "Low Cost (MER)", body: "Passive index ETFs have very low MERs — typically 0.05–0.25%. No active research needed, costs passed to investors as savings.", color: green },
    { icon: "⏱️", title: "Intraday Liquidity", body: "Unlike mutual funds (priced once daily at 4 PM), ETFs trade on exchanges throughout the day at live market prices.", color: blue2 },
    { icon: "🔍", title: "Transparency", body: "ETF holdings are disclosed daily — investors know exactly what the fund owns at all times.", color: gold },
    { icon: "📊", title: "Passive Management", body: "Most ETFs track an index. No manager makes buy/sell decisions — the index determines holdings automatically.", color: purple },
    { icon: "🎯", title: "Tracking Error Risk", body: "The ETF's return may differ from the index due to MER, trading costs, and timing differences.", color: red },
    { icon: "📉", title: "Market Risk", body: "If the index falls, the ETF falls by the same amount — there is no active management to cushion losses.", color: red },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmin(200px,1fr))", gap: 10, margin: "28px 0" }}>
      {features.map((f, i) => (
        <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 16px", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: f.color }} />
          <div style={{ fontSize: "1.2rem", marginBottom: 8 }}>{f.icon}</div>
          <div style={{ fontSize: "0.82rem", fontWeight: 700, color: white, marginBottom: 4 }}>{f.title}</div>
          <div style={{ fontSize: "0.78rem", color: muted, lineHeight: 1.65 }}>{f.body}</div>
        </div>
      ))}
    </div>
  );
}

// ── S5: Seg Funds ──
function SegFundPanel() {
  const [feature, setFeature] = useState(0);
  const features = [
    {
      icon: "🛡️", name: "Maturity Guarantee", color: green,
      desc: "Guarantees that at the end of the maturity period (typically 10 years), the investor will receive a minimum of 75% or 100% of their premiums paid — regardless of market performance.",
      example: "Investor puts in $100,000. Market crashes, fund is worth $60,000. With 75% guarantee: receives $75,000. With 100% guarantee: receives $100,000.",
    },
    {
      icon: "💀", name: "Death Benefit", color: purple,
      desc: "If the annuitant dies before maturity, the named beneficiary receives the greater of: (a) the guaranteed amount, or (b) the current market value of the fund.",
      example: "Fund worth $130,000, guarantee is $100,000. Beneficiary receives $130,000. Fund worth $80,000, guarantee is $100,000. Beneficiary receives $100,000.",
    },
    {
      icon: "⚖️", name: "Creditor Protection", color: gold,
      desc: "Because seg funds are insurance contracts, the invested assets may be protected from creditors in bankruptcy — unlike mutual funds, which can be seized by creditors.",
      example: "A business owner facing insolvency may have their seg fund protected if a family member is named as beneficiary. Regular mutual fund assets would not have this protection.",
    },
    {
      icon: "🏛️", name: "Estate Planning", color: blue2,
      desc: "Seg funds bypass the probate process. The death benefit is paid directly to the named beneficiary — faster, private, and without probate fees.",
      example: "Estate worth $500,000 in mutual funds goes through probate (months, public record, fees). Seg fund death benefit paid directly to beneficiary within days, privately.",
    },
  ];
  const f = features[feature];

  return (
    <div style={{ margin: "28px 0" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 8, marginBottom: 14 }}>
        {features.map((ft, i) => (
          <button key={i} onClick={() => setFeature(i)} style={{ background: feature === i ? navy4 : navy3, border: `1px solid ${feature === i ? ft.color + "55" : border}`, borderRadius: 8, padding: "14px 10px", cursor: "pointer", fontFamily: "inherit", fontSize: "0.75rem", fontWeight: 700, color: feature === i ? ft.color : muted, transition: "all 0.2s", textAlign: "center" }}>
            <div style={{ fontSize: "1.3rem", marginBottom: 6 }}>{ft.icon}</div>
            {ft.name}
          </button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${f.color}44`, borderRadius: 8, padding: 24 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 10 }}>{f.icon} {f.name}</div>
        <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75, marginBottom: 14 }}>{f.desc}</div>
        <div style={{ background: navy3, borderLeft: `3px solid ${f.color}`, borderRadius: 6, padding: "12px 16px", fontSize: "0.82rem", color: muted, lineHeight: 1.7 }}>
          📌 Example: {f.example}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
        <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "16px 18px" }}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, marginBottom: 8 }}>Seg Fund vs. Mutual Fund</div>
          {[["Insurance Contract?", "✓ Yes", "✗ No"], ["Maturity Guarantee?", "✓ Yes", "✗ No"], ["CDIC Insured?", "✗ No", "✗ No"], ["Creditor Protection?", "✓ Possible", "✗ No"], ["Bypasses Probate?", "✓ Yes", "✗ No"], ["Regulator", "Insurance (FSRA)", "Securities"]].map(([feat, seg, mf], i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4, padding: "5px 0", borderBottom: `1px solid ${border}` }}>
              <span style={{ fontSize: "0.75rem", color: muted }}>{feat}</span>
              <span style={{ fontSize: "0.75rem", color: green, fontWeight: 700 }}>{seg}</span>
              <span style={{ fontSize: "0.75rem", color: red }}>{mf}</span>
            </div>
          ))}
        </div>
        <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "16px 18px" }}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, marginBottom: 8 }}>Key Trade-Offs</div>
          {[
            { pro: "Maturity & death guarantees", con: "Higher MER than mutual funds" },
            { pro: "Creditor protection", con: "10-year lock-in for guarantees" },
            { pro: "Bypasses probate", con: "Not CDIC insured" },
            { pro: "Named beneficiary payouts", con: "Regulated differently (insurance)" },
          ].map((item, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, padding: "5px 0", borderBottom: `1px solid ${border}` }}>
              <div style={{ fontSize: "0.72rem", color: green }}>✓ {item.pro}</div>
              <div style={{ fontSize: "0.72rem", color: red }}>✗ {item.con}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Quiz ──
const questions = [
  { q: "Which of the following is a unique feature of Segregated Funds compared to Mutual Funds?", opts: ["Professional Management", "Diversification", "Maturity and Death Benefit Guarantees", "Daily Liquidity"], correct: 2, fb: { y: "Correct! Seg funds are insurance contracts — they offer a maturity guarantee (75% or 100% of premiums returned) and a death benefit. Regular mutual funds offer neither.", n: "Not quite. The unique features of seg funds are the maturity guarantee and death benefit — both insurance features unavailable in regular mutual funds." } },
  { q: "What is a 'High-Water Mark' in the context of a hedge fund?", opts: ["The maximum risk the fund can take", "A feature ensuring the manager is paid incentive fees only on new profits", "The highest value the fund is guaranteed to return", "The maximum number of investors allowed"], correct: 1, fb: { y: "Correct! The high-water mark means the manager earns zero incentive fee until the NAV surpasses its previous peak. They can't charge performance fees just for recovering previous losses.", n: "Not quite. The high-water mark protects investors by ensuring the manager is only paid on genuine new profits — not for recovering a previous NAV decline." } },
  { q: "Principal-Protected Notes (PPNs) are issued by:", opts: ["The Bank of Canada", "Mutual Fund Corporations", "Chartered Banks", "Insurance Companies"], correct: 2, fb: { y: "Correct! PPNs are debt instruments issued by chartered banks. The bank acts as the guarantor of the principal repayment at maturity.", n: "Not quite. PPNs are issued by chartered banks — not the central bank, fund companies, or insurers. The bank guarantees the principal." } },
  { q: "An investor with $50,000 who doesn't meet the Accredited Investor criteria could access hedge fund strategies through:", opts: ["A standard Money Market Fund", "An Alternative Mutual Fund ('Liquid Alt')", "A Segregated Fund", "They cannot access these strategies at all"], correct: 1, fb: { y: "Correct! Liquid Alts (Alternative Mutual Funds) are regulated mutual funds that use hedge fund strategies (leverage, shorting) and are accessible to retail investors without meeting accredited investor thresholds.", n: "Not quite. Liquid Alts provide retail investor access to hedge fund strategies within a regulated mutual fund wrapper. No accredited investor status needed." } },
  { q: "Exchange-Traded Funds (ETFs) typically trade at:", opts: ["The end-of-day NAVPU", "A fixed price of $10", "Market prices throughout the trading day", "A discount to NAV"], correct: 2, fb: { y: "Correct! ETFs trade on exchanges like stocks — buyers and sellers set prices throughout the day. This is a key advantage over mutual funds, which are priced only once after the 4 PM close.", n: "Not quite. ETFs trade intraday at market prices driven by supply and demand — not at a fixed price or end-of-day NAV." } },
  { q: "Which product offers potential creditor protection in the event of bankruptcy?", opts: ["ETF", "Closed-End Fund", "Segregated Fund", "Principal-Protected Note"], correct: 2, fb: { y: "Correct! Because seg funds are insurance contracts, the invested assets may be shielded from creditors in bankruptcy — particularly if a family member is named as beneficiary.", n: "Not quite. Creditor protection is unique to segregated funds — because they are insurance contracts, not securities. ETFs, closed-end funds, and PPNs offer no such protection." } },
  { q: "Closed-End Funds frequently trade at a:", opts: ["Premium to NAV", "Discount to NAV", "Fixed NAV", "Guaranteed price"], correct: 1, fb: { y: "Correct! Closed-end fund shares often trade below their calculated NAV — a discount. This is common due to limited liquidity, investor sentiment, and lack of a redemption mechanism at NAV.", n: "Not quite. Closed-end fund shares frequently trade at a discount to NAV because price is set by supply and demand on the exchange, not by the underlying asset value." } },
  { q: "Which hedge fund strategy is considered to have the lowest market exposure?", opts: ["Directional", "Event-Driven", "Relative Value", "Long-Only Equity"], correct: 2, fb: { y: "Correct! Relative Value strategies (e.g., arbitrage) involve simultaneously buying and selling related securities — netting out most market exposure. Returns depend on the spread, not market direction.", n: "Not quite. Relative Value has the lowest market exposure — it pairs long and short positions to eliminate directional market risk. Directional has the highest market exposure." } },
  { q: "Are PPNs covered by CDIC insurance?", opts: ["Yes, up to $100,000", "Yes, if the term is less than 5 years", "No, they are not covered", "Only the interest portion is covered"], correct: 2, fb: { y: "Correct! PPNs are not deposit instruments — they are structured notes. CDIC only covers eligible deposits at member institutions. PPN holders rely solely on the issuing bank's creditworthiness.", n: "Not quite. PPNs are NOT covered by CDIC. They are structured notes, not bank deposits. The only protection is the creditworthiness of the issuing bank." } },
  { q: "A 'Hurdle Rate' in a hedge fund refers to:", opts: ["The speed at which an ETF trades", "The minimum return a hedge fund must earn before charging an incentive fee", "The maximum loss a PPN can suffer", "The tax rate applied to segregated fund gains"], correct: 1, fb: { y: "Correct! The hurdle rate is a performance threshold. If the fund earns less than the hurdle (e.g., 5%), the manager receives no incentive fee — only the management fee.", n: "Not quite. The hurdle rate is a minimum return benchmark. The manager's incentive fee only applies to returns above this threshold — aligning manager and investor interests." } },
];

function Quiz() {
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const score = Object.values(answered).filter(a => a.correct).length;
  const total = questions.length;
  function answer(idx) { if (answered[current] !== undefined) return; setAnswered({ ...answered, [current]: { chosen: idx, correct: idx === questions[current].correct } }); }
  function next() { if (current < total - 1) setCurrent(current + 1); else setDone(true); }
  function retry() { setCurrent(0); setAnswered({}); setDone(false); }
  const q = questions[current], ans = answered[current];
  const pct = Math.round(score / total * 100);

  return (
    <div style={{ background: navy2, border: `1px solid ${border}`, borderRadius: 12, padding: 36, marginTop: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.3rem", color: white }}>Chapter 13 Quiz</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted }}>Question {current + 1} of {total}</div>
      </div>
      <div style={{ height: 4, background: navy4, borderRadius: 2, marginBottom: 28, overflow: "hidden" }}>
        <div style={{ height: "100%", background: `linear-gradient(90deg,${gold},${blue})`, borderRadius: 2, width: `${(current + 1) / total * 100}%`, transition: "width 0.4s ease" }} />
      </div>
      {!done ? (
        <>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.05rem", color: white, lineHeight: 1.55, marginBottom: 22 }}>{q.q}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {q.opts.map((opt, i) => {
              const isChosen = ans?.chosen === i, isCorrect = i === q.correct;
              let bg = navy3, bdr = border, col = textCol;
              if (ans) { if (isCorrect) { bg = "rgba(34,197,94,.08)"; bdr = green; col = "#4ade80"; } else if (isChosen) { bg = "rgba(239,68,68,.08)"; bdr = "#ef4444"; col = "#f87171"; } }
              return (
                <button key={i} onClick={() => answer(i)} disabled={!!ans}
                  style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 8, padding: "14px 18px", textAlign: "left", fontFamily: "inherit", fontSize: "0.88rem", color: col, cursor: ans ? "default" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 26, height: 26, flexShrink: 0, background: navy4, border: `1px solid ${border}`, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.72rem", color: muted }}>{["A","B","C","D"][i]}</span>
                  {opt}
                </button>
              );
            })}
          </div>
          {ans && (
            <>
              <div style={{ marginTop: 14, padding: "13px 16px", borderRadius: 7, fontSize: "0.84rem", lineHeight: 1.65, background: ans.correct ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)", border: `1px solid ${ans.correct ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.2)"}`, color: ans.correct ? "#86efac" : "#fca5a5" }}>
                {ans.correct ? "✓ " : "✗ "}{ans.correct ? q.fb.y : q.fb.n}
              </div>
              <button onClick={next} style={{ marginTop: 20, background: gold, color: navy, border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>
                {current < total - 1 ? "Next Question →" : "See My Results →"}
              </button>
            </>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ width: 110, height: 110, borderRadius: "50%", margin: "0 auto 20px", background: `conic-gradient(${gold} 0% ${pct}%, ${navy4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "absolute", inset: 14, borderRadius: "50%", background: navy2 }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1.5rem", fontWeight: 900, color: gold, zIndex: 1 }}>{score}/{total}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.5rem", color: white, marginBottom: 8 }}>
            {score === total ? "Perfect Score! 🏆" : score >= 8 ? "Strong Result ✦" : score >= 6 ? "Good Effort" : "Keep Studying"}
          </div>
          <div style={{ fontSize: "0.9rem", color: muted, marginBottom: 24 }}>
            {score === total ? "Flawless. You've mastered all alternative managed products." : score >= 8 ? "Excellent — review any missed questions." : score >= 6 ? "Good start. Revisit seg fund features, hedge fund fees, and PPN risks." : "Focus on the five products, their unique features, and who regulates each one."}
          </div>
          <button onClick={retry} style={{ background: "transparent", color: gold, border: `1px solid rgba(245,166,35,.35)`, padding: "10px 26px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      )}
    </div>
  );
}

// ── Glossary ──
const glossary = [
  { term: "Alternative Managed Products", def: "Professionally managed portfolios using complex strategies (leverage, derivatives, short selling) not permitted in conventional mutual funds." },
  { term: "Principal-Protected Note (PPN)", def: "A bank-issued debt instrument guaranteeing return of principal at maturity, with returns linked to an underlying asset (index, commodity, fund)." },
  { term: "Guarantor (PPN)", def: "The chartered bank that guarantees the principal repayment. Credit risk = the bank could fail." },
  { term: "Participation Rate", def: "The % of the underlying asset's return credited to the PPN investor (e.g., 60% means you receive 60% of the index gain)." },
  { term: "Performance Averaging", def: "PPN implicit cost — returns based on average monthly values rather than final value, reducing returns in rising markets." },
  { term: "Hedge Fund", def: "Lightly regulated investment pool using aggressive strategies (leverage, shorts, derivatives) to generate absolute returns regardless of market direction." },
  { term: "Relative Value Strategy", def: "Exploits pricing inefficiencies between related securities. Low market exposure — returns depend on spread, not market direction." },
  { term: "Event-Driven Strategy", def: "Profits from specific corporate events (mergers, bankruptcies). Medium market exposure." },
  { term: "Directional Strategy", def: "Bets on market direction using long/short positions. High market exposure (e.g., Long/Short Equity, Global Macro)." },
  { term: "Incentive Fee", def: "A share of profits (e.g., 20%) paid to the hedge fund manager — above the hurdle rate." },
  { term: "High-Water Mark", def: "Manager earns incentive fees only on new profits beyond the previous NAV peak. Protects investors from paying twice on recovered losses." },
  { term: "Hurdle Rate", def: "Minimum return the fund must earn before the manager charges an incentive fee. Aligns manager/investor interests." },
  { term: "Accredited Investor", def: "Individual with >$1M in financial assets OR >$200K annual income. Can access hedge funds via prospectus exemption." },
  { term: "Liquid Alt", def: "Alternative Mutual Fund — retail-accessible fund using hedge-fund strategies within a regulated mutual fund structure." },
  { term: "Closed-End Fund", def: "Issues a fixed number of shares at IPO; no new units. Trades on exchange at supply/demand prices — often at a discount to NAV." },
  { term: "Discount (Closed-End)", def: "When a closed-end fund's market price is below its NAV. Common due to limited liquidity and investor sentiment." },
  { term: "Exchange-Traded Fund (ETF)", def: "A basket of securities trading on an exchange like a stock. Usually passively tracks an index. Low MER, intraday liquidity, daily disclosure." },
  { term: "Tracking Error", def: "The difference between an ETF's return and its benchmark index return. Caused by MER, transaction costs, and cash drag." },
  { term: "Segregated Fund", def: "An insurance contract with investment features. Offers maturity guarantee, death benefit, creditor protection, and probate bypass." },
  { term: "Maturity Guarantee", def: "Seg fund feature: at maturity (typically 10 years), the investor receives at least 75% or 100% of premiums paid, regardless of market performance." },
  { term: "Death Benefit", def: "Seg fund feature: if the annuitant dies, the beneficiary receives the greater of the guaranteed amount or current market value." },
  { term: "Creditor Protection", def: "Seg fund advantage: invested assets may be protected from creditors in bankruptcy if a beneficiary is named. Not available with mutual funds." },
];

// ── Main Export ──
export default function Chapter13() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const fn = () => {
      const h = document.documentElement;
      setProgress((h.scrollTop || document.body.scrollTop) / (h.scrollHeight - h.clientHeight) * 100);
    };
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const tocItems = [
    ["01", "Product Overview — All Five Alternatives"],
    ["02", "Principal-Protected Notes (PPNs)"],
    ["03", "Hedge Funds — Strategies, Fees & Access"],
    ["04", "Closed-End Funds — Discount & Premium"],
    ["05", "Exchange-Traded Funds (ETFs)"],
    ["06", "Segregated Funds — Insurance Features"],
    ["🧮", "Incentive Fee & Tracking Error Calculators"],
    ["✦", "Knowledge Check Quiz"],
    ["✦", "Key Terms Glossary"],
  ];

  return (
    <div style={{ background: navy, minHeight: "100vh", fontFamily: "'Nunito Sans', sans-serif" }}>
      <div style={{ position: "fixed", top: 0, left: 0, height: 3, background: `linear-gradient(90deg,${gold},${blue})`, width: `${progress}%`, zIndex: 9999, transition: "width 0.15s ease" }} />
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px 80px" }}>

        <Reveal>
          <div style={{ padding: "80px 0 56px", borderBottom: `1px solid ${border}`, marginBottom: 56 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.25)", color: gold, fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "5px 14px", borderRadius: 100, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: gold, display: "inline-block" }} />
              IFC/CSC · Chapter 13
            </div>
            <h1 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(2.2rem,5vw,3.4rem)", fontWeight: 900, color: white, lineHeight: 1.15, marginBottom: 20 }}>
              Alternative <span style={{ color: gold }}>Managed Products</span>
            </h1>
            <p style={{ fontSize: "1.05rem", color: muted, maxWidth: 600, lineHeight: 1.8, marginBottom: 32 }}>
              PPNs, hedge funds, closed-end funds, ETFs, and segregated funds — their structures, strategies, fees, guarantees, and the key distinctions that appear on every exam.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, paddingTop: 24, borderTop: `1px solid ${border}` }}>
              {[["📖","5","Product Types"],["⏱","~20 min","read"],["🎯","10","practice questions"],["🧮","2","calculators"],["📊","4","interactive tools"]].map(([icon,val,lbl],i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: blue }}>{icon}</span>
                  <span style={{ fontSize: "0.82rem", color: muted }}><strong style={{ color: textCol }}>{val}</strong> {lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${gold}`, borderRadius: 8, padding: "28px 32px", marginBottom: 56 }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "0.85rem", fontWeight: 700, color: gold, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>📋 In This Chapter</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {tocItems.map(([num, label], i) => (
                <li key={i} style={{ padding: "6px 0", borderBottom: i < tocItems.length - 1 ? `1px solid ${border}` : "none", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontFamily: "monospace", fontSize: "0.7rem", color: muted, width: 24, flexShrink: 0 }}>{num}</span>
                  <span style={{ color: textCol, fontSize: "0.9rem", fontWeight: 600 }}>{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        {/* S1 */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 01</span><span style={ss.badge}>Overview</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Alternative Managed Products</h2>
            <p style={ss.prose}>
              Unlike conventional mutual funds, alternative managed products can use <span style={{ color: gold, fontWeight: 600 }}>leverage, derivatives, short selling, and other complex strategies</span> not permitted under standard NI 81-102 rules. Click any product below to see its full profile:
            </p>
          </Reveal>
          <Reveal delay={0.1}><ProductOverview /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S2 PPNs */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 02</span><span style={ss.badge}>PPNs</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Principal-Protected Notes</h2>
            <p style={ss.prose}>
              A PPN is a <span style={{ color: gold, fontWeight: 600 }}>bank-issued debt instrument</span> that guarantees your principal back at maturity while linking potential returns to an underlying asset. The costs — both visible and hidden — are what exam questions focus on:
            </p>
          </Reveal>
          <Reveal delay={0.1}><PPNDetail /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="⚠️" accent={red}>
              <strong style={{ color: red }}>The Hidden Cost Trap:</strong> A PPN may offer only a <strong>60% participation rate</strong> in the index, use <strong>performance averaging</strong> (monthly average, not final value), and exclude <strong>dividends</strong> (price return only). Combined, these implicit costs can dramatically reduce your actual return compared to simply owning the index.
            </Callout>
          </Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S3 Hedge */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 03</span><span style={ss.badge}>Hedge Funds</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Hedge Funds</h2>
            <p style={ss.prose}>
              Hedge funds are lightly regulated pools of capital using aggressive strategies to generate <span style={{ color: gold, fontWeight: 600 }}>absolute returns</span> regardless of market conditions. Three strategy families, each with different market exposure:
            </p>
          </Reveal>
          <Reveal delay={0.1}><HedgeStrategies /></Reveal>
          <Reveal delay={0.15}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "0.95rem", color: white, margin: "24px 0 12px" }}>Who Can Invest?</div>
          </Reveal>
          <Reveal delay={0.2}><HedgeAccessPanel /></Reveal>
          <Reveal delay={0.3}><HedgeFeeCalc /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S4 Closed-End */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 04</span><span style={ss.badge}>Closed-End Funds</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Closed-End Funds</h2>
            <p style={ss.prose}>
              Closed-end funds issue a <span style={{ color: gold, fontWeight: 600 }}>fixed number of shares at IPO</span> — after that, no new money enters. Shares trade on an exchange like stocks. The price is set by supply and demand, not NAV — and it's usually a discount:
            </p>
          </Reveal>
          <Reveal delay={0.1}><ClosedEndViz /></Reveal>
          <Reveal delay={0.2}>
            <Callout icon="📌" accent={purple}>
              <strong style={{ color: purple }}>Discount vs. Premium:</strong> A closed-end fund trading at a <strong>discount</strong> means you can buy $1.00 of assets for less than $1.00. A <strong>premium</strong> means paying more than the underlying assets are worth. Discounts are common; premiums are rare and often short-lived.
            </Callout>
          </Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S5 ETFs */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 05</span><span style={ss.badge}>ETFs</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Exchange-Traded Funds (ETFs)</h2>
            <p style={ss.prose}>
              ETFs combine the diversification of mutual funds with the <span style={{ color: gold, fontWeight: 600 }}>intraday trading of stocks</span>. Most track an index passively, with low MERs and daily holdings disclosure:
            </p>
          </Reveal>
          <Reveal delay={0.1}><ETFFeatureGrid /></Reveal>
          <Reveal delay={0.2}><ETFTrackingCalc /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* S6 Seg Funds */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Section 06</span><span style={ss.badge}>Segregated Funds</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Segregated Funds</h2>
            <p style={ss.prose}>
              Segregated funds are <span style={{ color: gold, fontWeight: 600 }}>insurance contracts</span> sold by life insurance companies — not mutual funds. They hold investments but wrap them in insurance features that regular mutual funds cannot offer. Regulated by provincial insurance regulators (e.g., FSRA in Ontario), not securities commissions:
            </p>
          </Reveal>
          <Reveal delay={0.1}><SegFundPanel /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* Quiz */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Knowledge Check</span><span style={ss.badge}>10 Questions</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Test Your Understanding</h2>
            <p style={ss.prose}>10 questions covering all five alternative product types, hedge fund fee mechanics, ETF tracking error, and seg fund features from Chapter 13.</p>
          </Reveal>
          <Reveal delay={0.1}><Quiz /></Reveal>
        </div>

        <hr style={ss.hr} />

        {/* Glossary */}
        <div style={ss.section}>
          <Reveal>
            <div style={ss.eyebrow}><span style={ss.tag}>Quick Reference</span><span style={ss.badge}>Key Terms</span><div style={ss.line} /></div>
            <h2 style={ss.h2}>Glossary</h2>
            <p style={ss.prose}>All essential terms from Chapter 13 — PPNs, hedge funds, closed-end funds, ETFs, and segregated funds.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10, margin: "28px 0" }}>
              {glossary.map((g, i) => (
                <div key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "18px 20px", transition: "border-color 0.2s", cursor: "default" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(245,166,35,.3)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = border}>
                  <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: gold, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{g.term}</div>
                  <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{g.def}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

      </div>
    </div>
  );
}
